// The admin or manager invite Laila drafts (lailaChat's draft_staff_invite
// tool): what the card holds, what is left to set before it can go, and the
// body the app sends. The mobile app has the same logic
// (screens/AIChat/components/cards/staffInvite.ts).
import { SendStaffInviteRequest } from '../../../../api/types';

export interface StaffRole {
  id: string;
  name: string;
  resourceType: string;
  // Covers only the teams, sites or pay schedules picked for it.
  scoped: boolean;
  // Makes them a company owner, which only an owner may grant.
  owner: boolean;
}

export interface StaffModule {
  name: string;
  roles: StaffRole[];
  // null: no access.
  roleId: string | null;
  scope: string[];
  resources: { id: string; name: string }[];
  // The user may not see what a scoped role here could cover.
  resourcesUnavailable?: boolean;
  // What the user asked for that matched nothing.
  asked?: { role?: string; unmatchedScope?: string[] };
}

export interface StaffInviteData {
  companyId: string;
  email: string;
  emailValid: boolean;
  position: string;
  modules: StaffModule[];
  // Set by the app once the user acts on the card; none while it's a draft.
  state?: 'sending' | 'started' | 'failed' | 'cancelled';
  message?: string;
}

export type StaffInviteChange =
  | { type: 'email'; email: string }
  | { type: 'position'; position: string }
  | { type: 'role'; module: string; roleId: string | null }
  // Adds or takes away one team, site or pay schedule.
  | { type: 'scope'; module: string; id: string };

export const validEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

export const roleOf = (module: StaffModule) => module.roles.find(role => role.id === module.roleId);

// "sites", "teams", "pay schedules": what a module's scoped roles cover.
export const scopeNoun = (module: StaffModule) => {
  const type = (module.roles.find(role => role.scoped)?.resourceType || '').toLowerCase();
  if (type.includes('team')) {
    return 'teams';
  }
  if (type.includes('pay') || type.includes('schedule')) {
    return 'pay schedules';
  }
  return 'sites';
};

const updateModule = (
  data: StaffInviteData,
  name: string,
  change: (module: StaffModule) => StaffModule
) => ({
  ...data,
  modules: data.modules.map(module => (module.name === name ? change(module) : module)),
});

export const applyStaffChange = (data: StaffInviteData, change: StaffInviteChange): StaffInviteData => {
  switch (change.type) {
    case 'email':
      return { ...data, email: change.email, emailValid: validEmail(change.email) };
    case 'position':
      return { ...data, position: change.position };
    case 'role':
      return updateModule(data, change.module, module => {
        const role = module.roles.find(item => item.id === change.roleId);
        return {
          ...module,
          roleId: role?.id ?? null,
          scope: role?.scoped ? module.scope : [],
          asked: undefined,
        };
      });
    case 'scope':
      return updateModule(data, change.module, module => ({
        ...module,
        scope: module.scope.includes(change.id)
          ? module.scope.filter(id => id !== change.id)
          : [...module.scope, change.id],
      }));
  }
};

export const hasOwnerRole = (data: StaffInviteData) => data.modules.some(module => roleOf(module)?.owner);

// What stops the invite going, in the words the card shows.
export const staffProblems = (data: StaffInviteData): string[] => {
  const problems: string[] = [];
  if (!data.emailValid) {
    problems.push('Add their email address');
  }
  if (!data.position.trim()) {
    problems.push('Add their position');
  }
  const withRoles = data.modules.filter(module => module.roleId);
  if (!withRoles.length) {
    problems.push('Give them a role in at least one module');
  }
  withRoles
    .filter(module => roleOf(module)?.scoped && !module.scope.length)
    .forEach(module =>
      problems.push(
        module.resourcesUnavailable
          ? `You can’t see the ${scopeNoun(module)} for ${module.name}: pick another role`
          : `Pick at least one of the ${scopeNoun(module)} for ${module.name}`
      )
    );
  return problems;
};

export const staffPayload = (data: StaffInviteData): SendStaffInviteRequest => ({
  email: data.email.trim().toLowerCase(),
  staff_payload: {
    position_name: data.position.trim(),
    roles: data.modules
      .filter(module => module.roleId)
      .map(module => {
        const role = roleOf(module)!;
        return {
          role_id: role.id,
          resource_type: role.resourceType,
          // A role over the whole company is saved against the company itself.
          resource_ids: role.scoped ? module.scope : [data.companyId],
        };
      }),
  },
});

// "Time & Attendance: Manager (Riverside Wharf, Canary Dock)", for Laila and the receipt.
export const staffAccess = (data: StaffInviteData) =>
  data.modules
    .filter(module => module.roleId)
    .map(module => {
      const role = roleOf(module)!;
      const names = module.scope
        .map(id => module.resources.find(resource => resource.id === id)?.name)
        .filter(Boolean);
      return `${module.name}: ${role.name}${role.scoped && names.length ? ` (${names.join(', ')})` : ''}`;
    })
    .join('; ');
